import React, { useState, useEffect, Component } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Settings, ArrowLeft, Upload, Download, Paperclip, X, AlertTriangle, FileText, Undo2, Euro } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Error Boundary per catturare errori di rendering
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Errore nel componente:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="p-6 bg-red-50 border border-red-200 rounded-lg m-4">
          <div className="flex items-center gap-2 text-red-700 mb-2">
            <AlertTriangle className="w-5 h-5" />
            <span className="font-semibold">Si è verificato un errore</span>
          </div>
          <p className="text-red-600 text-sm mb-3">
            {this.state.error?.message || 'Errore sconosciuto'}
          </p>
          <button 
            onClick={() => this.setState({ hasError: false, error: null })}
            className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700"
          >
            Riprova
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const ProgettiPage = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [progetti, setProgetti] = useState([]);
  const [progettiFiltered, setProgettiFiltered] = useState([]);
  const [filtroStato, setFiltroStato] = useState('tutti');
  const [ordini, setOrdini] = useState([]);
  const [utenti, setUtenti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [editingProgetto, setEditingProgetto] = useState(null);
  const [selectedProgetto, setSelectedProgetto] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadDescrizione, setUploadDescrizione] = useState('');
  const [fatturaDialogOpen, setFatturaDialogOpen] = useState(false);
  const [datiFattura, setDatiFattura] = useState(null);
  const [codiceOda, setCodiceOda] = useState('');
  const [accordiQuadro, setAccordiQuadro] = useState([]);
  const [tipoCollegamento, setTipoCollegamento] = useState('ordine'); // 'ordine' o 'accordo_quadro'
  
  // Stato per giustificazione modifica date
  const [modificaDataDialogOpen, setModificaDataDialogOpen] = useState(false);
  const [modificaDataPendente, setModificaDataPendente] = useState(null);  // {campo, vecchioValore, nuovoValore}
  const [giustificazioneModifica, setGiustificazioneModifica] = useState('');
  
  // Listino attività dinamico caricato dall'AQ selezionato
  const [listinoAttivitaAQ, setListinoAttivitaAQ] = useState([]);
  const [loadingListino, setLoadingListino] = useState(false);
  
  // Stato per dialog Genera Fattura con selezione SAL
  const [generaFatturaDialogOpen, setGeneraFatturaDialogOpen] = useState(false);
  const [progettoPerFattura, setProgettoPerFattura] = useState(null);
  const [salListOrdine, setSalListOrdine] = useState([]);
  const [selectedSalIndex, setSelectedSalIndex] = useState(null);
  const [loadingSal, setLoadingSal] = useState(false);
  
  // Funzione per verificare se l'utente può vedere i costi
  // Visibile a: admin, master, guest con ruolo amministrativo
  const canViewCosti = () => {
    if (user.ruolo === 'admin' || user.ruolo === 'master') return true;
    // Guest con ambito amministrativo (supporta sia ambito singolo che ambiti array)
    if (user.ambito === 'amministrativo') return true;
    if (user.ambiti && Array.isArray(user.ambiti) && user.ambiti.includes('amministrativo')) return true;
    return false;
  };
  
  // Helper per verificare se un utente ha un determinato ambito (supporta ambiti multipli)
  const userHasAmbito = (u, ambito) => {
    if (u.ambiti && u.ambiti.length > 0) {
      return u.ambiti.includes(ambito);
    }
    return u.ambito === ambito;
  };
  
  const [formData, setFormData] = useState({
    ordine_lavoro_id: '',
    accordo_quadro_id: '',
    nome_progetto: '',
    tipo_impianto: '',
    tipologia_progetto: '',  // Preliminare, Definitivo, Esecutivo, As-Built
    attivita_accordo: '',  // ID attività da Tabella Prezzi per Accordi Quadro
    varianti: '',  // Eventuali varianti
    importo_progetto: '',
    voce_listino_id: '',  // ID voce selezionata dal listino
    voce_listino_nome: '',  // Nome voce per riferimento
    stato: 'non_iniziato',
    data_inizio: '',
    data_fine_prevista: '',
    data_fine_effettiva: '',
    ingegnere_responsabile_id: '',
    referenti_progetto_ids: [],
    note_tecniche: '',
    codice_progetto_cliente: '',  // Per ordini multiple_commesse
    codice_progetto_main_solution: '',  // Per ordini multiple_commesse
    storico_modifiche_date: []  // Storico modifiche date con giustificazione
  });

  useEffect(() => {
    fetchProgetti();
    fetchOrdini();
    fetchUtenti();
    fetchAccordiQuadro();
  }, []);

  useEffect(() => {
    // Applica filtro stato
    if (filtroStato === 'tutti') {
      setProgettiFiltered(progetti);
    } else {
      setProgettiFiltered(progetti.filter(p => p.stato === filtroStato));
    }
  }, [progetti, filtroStato]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const fetchProgetti = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/progetti`, getAuthHeaders());
      setProgetti(response.data);
    } catch (error) {
      console.error('Errore caricamento progetti:', error);
      if (error.response?.status === 403) {
        toast.error('Non hai i permessi per accedere a questa sezione');
        navigate('/');
      } else {
        toast.error('Errore nel caricamento dei progetti');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchOrdini = async () => {
    try {
      const response = await axios.get(`${API}/ordini-lavoro`, getAuthHeaders());
      setOrdini(response.data);
    } catch (error) {
      console.error('Errore caricamento ordini:', error);
    }
  };

  const fetchUtenti = async () => {
    try {
      const response = await axios.get(`${API}/users`, getAuthHeaders());
      setUtenti(response.data);
    } catch (error) {
      console.error('Errore caricamento utenti:', error);
    }
  };
  
  // Carica il listino attività dall'AQ selezionato
  const fetchListinoAttivita = async (accordoId) => {
    if (!accordoId) {
      setListinoAttivitaAQ([]);
      return;
    }
    
    setLoadingListino(true);
    try {
      const response = await axios.get(`${API}/accordi-quadro/${accordoId}/listino`, getAuthHeaders());
      // Mappa il listino in formato compatibile con le nuove proprietà
      const listino = (response.data.listino || []).map(voce => ({
        id: voce.id,
        nome: voce.descrizione,
        costo: voce.prezzo || 0,
        unita_misura: voce.unita_misura || 'euro',
        ore: voce.ore || 0
      }));
      setListinoAttivitaAQ(listino);
    } catch (error) {
      console.error('Errore caricamento listino:', error);
      setListinoAttivitaAQ([]);
    } finally {
      setLoadingListino(false);
    }
  };
  
  // Filtra utenti disponibili per ingegnere responsabile in base al tipo di progetto
  const getUtentiResponsabile = () => {
    if (tipoCollegamento === 'accordo_quadro') {
      // Per Accordi Quadro: solo Master fattibilità
      return utenti.filter(u => u.ruolo === 'master' && userHasAmbito(u, 'fattibilita'));
    } else {
      // Per Ordini: ingegneri e admin
      return utenti.filter(u => userHasAmbito(u, 'ingegnere') || u.ruolo === 'admin');
    }
  };
  
  // Filtra utenti disponibili per referenti progetto in base al tipo di progetto
  const getUtentiReferenti = () => {
    if (tipoCollegamento === 'accordo_quadro') {
      // Per Accordi Quadro: Guest fattibilità, Master ingegneria, Master fattibilità
      return utenti.filter(u => 
        (u.ruolo === 'guest' && userHasAmbito(u, 'fattibilita')) ||
        (u.ruolo === 'master' && userHasAmbito(u, 'ingegnere')) ||
        (u.ruolo === 'master' && userHasAmbito(u, 'fattibilita')) ||
        u.ruolo === 'admin'
      );
    } else {
      // Per Ordini: ingegneri e admin
      return utenti.filter(u => userHasAmbito(u, 'ingegnere') || u.ruolo === 'admin');
    }
  };

  const fetchAccordiQuadro = async () => {
    try {
      const response = await axios.get(`${API}/accordi-quadro`, getAuthHeaders());
      setAccordiQuadro(response.data.filter(a => a.stato === 'attivo'));
    } catch (error) {
      console.error('Errore caricamento accordi quadro:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Se stiamo modificando un progetto esistente da Accordo Quadro e cambiano le date
    if (editingProgetto && tipoCollegamento === 'accordo_quadro' && 
        (name === 'data_fine_prevista' || name === 'data_inizio')) {
      const vecchioValore = formData[name];
      
      // Se la data è effettivamente diversa, richiedi giustificazione
      if (vecchioValore && vecchioValore !== value) {
        setModificaDataPendente({
          campo: name,
          vecchioValore: vecchioValore,
          nuovoValore: value,
          label: name === 'data_fine_prevista' ? 'Data Fine Prevista' : 'Data Inizio'
        });
        setGiustificazioneModifica('');
        setModificaDataDialogOpen(true);
        return;  // Non aggiornare subito, aspetta la giustificazione
      }
    }
    
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  // Conferma modifica data con giustificazione
  const handleConfermaModificaData = () => {
    if (!giustificazioneModifica.trim()) {
      toast.error('Inserisci una giustificazione per la modifica della data');
      return;
    }
    
    const nuovaModifica = {
      data_modifica: new Date().toISOString(),
      campo: modificaDataPendente.campo,
      valore_precedente: modificaDataPendente.vecchioValore,
      nuovo_valore: modificaDataPendente.nuovoValore,
      giustificazione: giustificazioneModifica,
      utente: `${user.nome} ${user.cognome}`
    };
    
    setFormData(prev => ({
      ...prev,
      [modificaDataPendente.campo]: modificaDataPendente.nuovoValore,
      storico_modifiche_date: [...(prev.storico_modifiche_date || []), nuovaModifica]
    }));
    
    setModificaDataDialogOpen(false);
    setModificaDataPendente(null);
    setGiustificazioneModifica('');
    toast.success('Data modificata con giustificazione registrata');
  };
  
  // Annulla modifica data
  const handleAnnullaModificaData = () => {
    setModificaDataDialogOpen(false);
    setModificaDataPendente(null);
    setGiustificazioneModifica('');
  };

  // Funzione per verificare se l'utente può generare fattura per un progetto
  const canGeneraFattura = (progetto) => {
    if (user.ruolo === 'admin') return true;
    // Ingegnere responsabile del progetto
    if (progetto.ingegnere_responsabile_id === user.id) return true;
    return false;
  };

  // Apri dialog genera fattura
  const handleOpenGeneraFattura = async (progetto) => {
    if (!progetto.ordine_lavoro_id) {
      toast.error('Questo progetto non ha un ordine di lavoro collegato');
      return;
    }
    
    setProgettoPerFattura(progetto);
    setLoadingSal(true);
    setSelectedSalIndex(null);
    setGeneraFatturaDialogOpen(true);
    
    try {
      // Carica l'ordine di lavoro per ottenere la lista SAL
      const response = await axios.get(`${API}/ordini-lavoro/${progetto.ordine_lavoro_id}`, getAuthHeaders());
      const ordine = response.data;
      setSalListOrdine(ordine.sal_list || []);
    } catch (error) {
      console.error('Errore caricamento SAL:', error);
      toast.error('Errore nel caricamento dei SAL dell\'ordine');
      setSalListOrdine([]);
    } finally {
      setLoadingSal(false);
    }
  };

  // Conferma generazione fattura - cambia stato SAL
  const handleConfermaGeneraFattura = async () => {
    if (selectedSalIndex === null) {
      toast.error('Seleziona un SAL');
      return;
    }
    
    try {
      // Aggiorna lo stato del SAL selezionato a "da_fatturare"
      const ordineId = progettoPerFattura.ordine_lavoro_id;
      const response = await axios.get(`${API}/ordini-lavoro/${ordineId}`, getAuthHeaders());
      const ordine = response.data;
      
      // Modifica lo stato del SAL
      const updatedSalList = [...ordine.sal_list];
      updatedSalList[selectedSalIndex] = {
        ...updatedSalList[selectedSalIndex],
        stato: 'da_fatturare'
      };
      
      // Aggiorna l'ordine
      await axios.put(`${API}/ordini-lavoro/${ordineId}`, {
        ...ordine,
        sal_list: updatedSalList
      }, getAuthHeaders());
      
      toast.success(`SAL ${selectedSalIndex + 1} impostato come "Da Fatturare"!`);
      setGeneraFatturaDialogOpen(false);
      setProgettoPerFattura(null);
      setSelectedSalIndex(null);
      setSalListOrdine([]);
      
    } catch (error) {
      console.error('Errore aggiornamento SAL:', error);
      toast.error('Errore nell\'aggiornamento del SAL');
    }
  };

  // Annulla stato SAL (riporta a non_fatturabile)
  const handleAnnullaStatoSal = async (salIndex) => {
    if (!window.confirm('Vuoi annullare lo stato di fatturazione di questo SAL?')) return;
    
    try {
      const ordineId = progettoPerFattura.ordine_lavoro_id;
      const response = await axios.get(`${API}/ordini-lavoro/${ordineId}`, getAuthHeaders());
      const ordine = response.data;
      
      const updatedSalList = [...ordine.sal_list];
      updatedSalList[salIndex] = {
        ...updatedSalList[salIndex],
        stato: 'non_fatturabile'
      };
      
      await axios.put(`${API}/ordini-lavoro/${ordineId}`, {
        ...ordine,
        sal_list: updatedSalList
      }, getAuthHeaders());
      
      // Aggiorna la lista locale
      setSalListOrdine(updatedSalList);
      toast.success('Stato SAL annullato');
      
    } catch (error) {
      console.error('Errore annullamento SAL:', error);
      toast.error('Errore nell\'annullamento');
    }
  };

  const resetForm = () => {
    setTipoCollegamento('ordine');
    setFormData({
      ordine_lavoro_id: '',
      accordo_quadro_id: '',
      nome_progetto: '',
      tipo_impianto: '',
      tipologia_progetto: '',
      attivita_accordo: '',
      varianti: '',
      importo_progetto: '',
      voce_listino_id: '',
      voce_listino_nome: '',
      stato: 'non_iniziato',
      data_inizio: '',
      data_fine_prevista: '',
      data_fine_effettiva: '',
      ingegnere_responsabile_id: '',
      referenti_progetto_ids: [],
      note_tecniche: '',
      codice_progetto_cliente: '',
      codice_progetto_main_solution: '',
      storico_modifiche_date: []
    });
    setEditingProgetto(null);
    setListinoAttivitaAQ([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validazione frontend
    if (!formData.nome_progetto.trim()) {
      toast.error('Il nome progetto è obbligatorio');
      return;
    }
    
    if (!formData.ingegnere_responsabile_id) {
      toast.error('Seleziona un ingegnere responsabile');
      return;
    }
    
    if (tipoCollegamento === 'ordine' && !formData.ordine_lavoro_id) {
      toast.error('Seleziona un ordine di lavoro');
      return;
    }
    
    if (tipoCollegamento === 'accordo_quadro' && !formData.accordo_quadro_id) {
      toast.error('Seleziona un accordo quadro');
      return;
    }
    
    try {
      // Prepara i dati in base al tipo di collegamento
      const submitData = { ...formData };
      
      // Rimuovi campi che non sono nel modello ProgettoCreate
      delete submitData.storico_modifiche_date;
      
      // Converti importo_progetto in numero (sempre)
      submitData.importo_progetto = parseFloat(formData.importo_progetto) || 0;
      
      // Converti stringhe vuote in null per campi opzionali
      if (submitData.ordine_lavoro_id === '') submitData.ordine_lavoro_id = null;
      if (submitData.accordo_quadro_id === '') submitData.accordo_quadro_id = null;
      if (submitData.voce_listino_id === '') submitData.voce_listino_id = null;
      if (submitData.voce_listino_nome === '') submitData.voce_listino_nome = null;
      if (submitData.attivita_accordo === '') submitData.attivita_accordo = null;
      if (submitData.varianti === '') submitData.varianti = null;
      if (submitData.data_inizio === '') submitData.data_inizio = null;
      if (submitData.data_fine_prevista === '') submitData.data_fine_prevista = null;
      if (submitData.data_fine_effettiva === '') submitData.data_fine_effettiva = null;
      if (submitData.codice_progetto_cliente === '') submitData.codice_progetto_cliente = null;
      if (submitData.codice_progetto_main_solution === '') submitData.codice_progetto_main_solution = null;
      
      if (tipoCollegamento === 'accordo_quadro') {
        // Per accordi quadro, rimuovi ordine_lavoro_id
        submitData.ordine_lavoro_id = null;
        
        // Trova l'accordo per prendere il cliente
        const accordo = accordiQuadro.find(a => a.id === formData.accordo_quadro_id);
        if (accordo) {
          submitData.cliente_id = accordo.cliente_id;
        }
      } else {
        // Per ordini, rimuovi accordo_quadro_id
        submitData.accordo_quadro_id = null;
      }
      
      if (editingProgetto) {
        await axios.put(`${API}/progetti/${editingProgetto.id}`, submitData, getAuthHeaders());
        toast.success('Progetto aggiornato');
      } else {
        await axios.post(`${API}/progetti`, submitData, getAuthHeaders());
        toast.success('Progetto creato');
      }
      
      setDialogOpen(false);
      resetForm();
      fetchProgetti();
      fetchAccordiQuadro(); // Ricarica per aggiornare castelletto
    } catch (error) {
      console.error('Errore salvataggio:', error);
      // Gestisci errori di validazione FastAPI (possono essere oggetti o array)
      const errorDetail = error.response?.data?.detail;
      let errorMessage = 'Errore nel salvataggio';
      
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (Array.isArray(errorDetail)) {
        // Errori di validazione Pydantic
        errorMessage = errorDetail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
      } else if (errorDetail && typeof errorDetail === 'object') {
        errorMessage = errorDetail.msg || errorDetail.message || JSON.stringify(errorDetail);
      }
      
      toast.error(errorMessage);
    }
  };

  const handleEdit = (progetto) => {
    setEditingProgetto(progetto);
    const referentiIds = progetto.referenti_progetto ? progetto.referenti_progetto.map(r => r.user_id) : [];
    
    // Determina il tipo di collegamento
    if (progetto.accordo_quadro_id) {
      setTipoCollegamento('accordo_quadro');
      // Carica il listino dell'AQ per mostrare le attività
      fetchListinoAttivita(progetto.accordo_quadro_id);
    } else {
      setTipoCollegamento('ordine');
      setListinoAttivitaAQ([]);
    }
    
    setFormData({
      ordine_lavoro_id: progetto.ordine_lavoro_id || '',
      accordo_quadro_id: progetto.accordo_quadro_id || '',
      nome_progetto: progetto.nome_progetto,
      tipo_impianto: progetto.tipo_impianto,
      tipologia_progetto: progetto.tipologia_progetto || '',
      attivita_accordo: progetto.attivita_accordo || '',
      voce_listino_nome: progetto.voce_listino_nome || '',
      varianti: progetto.varianti || '',
      importo_progetto: progetto.importo_progetto?.toString() || '',
      stato: progetto.stato,
      data_inizio: progetto.data_inizio || '',
      data_fine_prevista: progetto.data_fine_prevista || '',
      data_fine_effettiva: progetto.data_fine_effettiva || '',
      ingegnere_responsabile_id: progetto.ingegnere_responsabile_id,
      referenti_progetto_ids: referentiIds,
      note_tecniche: progetto.note_tecniche || '',
      storico_modifiche_date: progetto.storico_modifiche_date || [],
      // Campi per ordini con più commesse
      codice_progetto_cliente: progetto.codice_progetto_cliente || '',
      codice_progetto_main_solution: progetto.codice_progetto_main_solution || ''
    });
    setDialogOpen(true);
  };

  const handleToggleReferente = (userId) => {
    setFormData(prev => {
      const isSelected = prev.referenti_progetto_ids.includes(userId);
      if (isSelected) {
        return { ...prev, referenti_progetto_ids: prev.referenti_progetto_ids.filter(id => id !== userId) };
      } else {
        return { ...prev, referenti_progetto_ids: [...prev.referenti_progetto_ids, userId] };
      }
    });
  };

  const handleDelete = async (progettoId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo progetto?')) return;
    
    try {
      await axios.delete(`${API}/progetti/${progettoId}`, getAuthHeaders());
      toast.success('Progetto eliminato');
      fetchProgetti();
    } catch (error) {
      console.error('Errore eliminazione:', error);
      toast.error('Errore nell\'eliminazione');
    }
  };

  const handleViewDetails = async (progetto) => {
    setSelectedProgetto(progetto);
    setDetailDialogOpen(true);
  };

  const handleUploadAllegato = async (progettoId) => {
    if (!uploadFile) {
      toast.error('Seleziona un file');
      return;
    }

    const formData = new FormData();
    formData.append('file', uploadFile);

    try {
      await axios.post(
        `${API}/progetti/${progettoId}/allegati?descrizione=${encodeURIComponent(uploadDescrizione)}`,
        formData,
        {
          ...getAuthHeaders(),
          headers: {
            ...getAuthHeaders().headers,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      toast.success('Allegato caricato');
      setUploadFile(null);
      setUploadDescrizione('');
      fetchProgetti();
      const response = await axios.get(`${API}/progetti/${progettoId}`, getAuthHeaders());
      setSelectedProgetto(response.data);
    } catch (error) {
      console.error('Errore upload:', error);
      toast.error('Errore caricamento allegato');
    }
  };

  const handleDownloadAllegato = async (progettoId, allegatoId, nomeFile) => {
    try {
      const response = await axios.get(
        `${API}/progetti/${progettoId}/allegati/${allegatoId}/download`,
        { ...getAuthHeaders(), responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', nomeFile);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Errore download:', error);
      toast.error('Errore download');
    }
  };

  const handleDeleteAllegato = async (progettoId, allegatoId) => {
    if (!window.confirm('Elimina allegato?')) return;

    try {
      await axios.delete(`${API}/progetti/${progettoId}/allegati/${allegatoId}`, getAuthHeaders());
      toast.success('Allegato eliminato');
      fetchProgetti();
      const response = await axios.get(`${API}/progetti/${progettoId}`, getAuthHeaders());
      setSelectedProgetto(response.data);
    } catch (error) {
      console.error('Errore eliminazione:', error);
      toast.error('Errore eliminazione');
    }
  };

  const handleGeneraFattura = async () => {
    if (!codiceOda || codiceOda.trim() === '') {
      toast.error('Il codice ODA è obbligatorio');
      return;
    }

    try {
      // Prima salva il codice ODA
      await axios.put(`${API}/progetti/${selectedProgetto.id}`, {
        codice_oda: codiceOda
      }, getAuthHeaders());

      // Poi genera la fattura
      const response = await axios.post(`${API}/progetti/${selectedProgetto.id}/genera-fattura`, {}, getAuthHeaders());
      setDatiFattura(response.data);
      toast.success('Fattura generata con successo!');
      fetchProgetti();
    } catch (error) {
      console.error('Errore generazione fattura:', error);
      const errorMsg = error.response?.data?.detail || 'Errore nella generazione della fattura';
      toast.error(errorMsg);
      setFatturaDialogOpen(false);
    }
  };

  const getStatoColor = (stato) => {
    const colors = {
      'non_iniziato': 'bg-slate-100 text-slate-700',
      'in_corso': 'bg-blue-100 text-blue-700',
      'in_validazione': 'bg-purple-100 text-purple-700',
      'in_revisione': 'bg-orange-100 text-orange-700',
      'completato': 'bg-green-100 text-green-700',
      'sospeso': 'bg-red-100 text-red-700',
      'as_built': 'bg-emerald-100 text-emerald-700'
    };
    return colors[stato] || colors.non_iniziato;
  };

  const getStatoLabel = (stato) => {
    const labels = {
      'non_iniziato': 'Non Iniziato',
      'in_corso': 'In Corso',
      'in_validazione': 'In Validazione',
      'in_revisione': 'In Revisione',
      'completato': 'Completato',
      'sospeso': 'Sospeso',
      'as_built': 'As Built'
    };
    return labels[stato] || stato;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-3 py-3 sm:px-6 sm:py-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-3">
              <Button variant="ghost" onClick={() => navigate('/')}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="hidden sm:block bg-indigo-600 p-3 rounded-xl shadow-md">
                <Settings className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-xl sm:text-3xl font-bold text-slate-900">Progetti (Ingegneria)</h1>
                <p className="text-sm text-slate-600">PaMa - Benvenuto, {user.nome}</p>
              </div>
            </div>
            
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-indigo-600 hover:bg-indigo-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Nuovo Progetto
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <ErrorBoundary>
                <DialogHeader>
                  <DialogTitle>{editingProgetto ? 'Modifica Progetto' : 'Nuovo Progetto'}</DialogTitle>
                </DialogHeader>
                
                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                  {/* Scelta tipo collegamento */}
                  {!editingProgetto && (
                    <div className="bg-slate-50 rounded-lg p-3">
                      <Label className="mb-2 block">Collega a:</Label>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant={tipoCollegamento === 'ordine' ? 'default' : 'outline'}
                          onClick={() => {
                            setTipoCollegamento('ordine');
                            setFormData(prev => ({ ...prev, accordo_quadro_id: '', importo_progetto: '', attivita_accordo: '' }));
                            setListinoAttivitaAQ([]);
                          }}
                          className={tipoCollegamento === 'ordine' ? 'bg-green-600 hover:bg-green-700' : ''}
                          size="sm"
                        >
                          📦 Ordine di Lavoro
                        </Button>
                        <Button
                          type="button"
                          variant={tipoCollegamento === 'accordo_quadro' ? 'default' : 'outline'}
                          onClick={() => {
                            setTipoCollegamento('accordo_quadro');
                            setFormData(prev => ({ ...prev, ordine_lavoro_id: '' }));
                          }}
                          className={tipoCollegamento === 'accordo_quadro' ? 'bg-indigo-600 hover:bg-indigo-700' : ''}
                          size="sm"
                        >
                          📋 Accordo Quadro
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Selezione Ordine di Lavoro */}
                  {tipoCollegamento === 'ordine' && (
                    <div>
                      <Label>Ordine di Lavoro *</Label>
                      <Select 
                        value={formData.ordine_lavoro_id} 
                        onValueChange={(value) => {
                          const selectedOrdine = ordini.find(o => o.id === value);
                          setFormData({
                            ...formData, 
                            ordine_lavoro_id: value,
                            // Pre-popola i codici dall'ordine se non è multiple_commesse
                            codice_progetto_cliente: selectedOrdine?.multiple_commesse ? '' : (selectedOrdine?.codice_progetto_cliente || ''),
                            codice_progetto_main_solution: selectedOrdine?.multiple_commesse ? '' : (selectedOrdine?.codice_progetto_main_solution || '')
                          });
                        }}
                        required={tipoCollegamento === 'ordine'}
                        disabled={editingProgetto}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Seleziona ordine" />
                        </SelectTrigger>
                        <SelectContent>
                          {ordini.map(ordine => (
                            <SelectItem key={ordine.id} value={ordine.id}>
                              {ordine.numero_ordine || 'N/D'} - {ordine.nome_progetto || 'Senza nome'}
                              {ordine.multiple_commesse && (
                                <span className="ml-2 text-purple-600">(Più Commesse)</span>
                              )}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Selezione Accordo Quadro */}
                  {tipoCollegamento === 'accordo_quadro' && (
                    <div className="space-y-3">
                      <div>
                        <Label>Accordo Quadro *</Label>
                        <Select 
                          value={formData.accordo_quadro_id} 
                          onValueChange={(value) => {
                            setFormData(prev => ({ ...prev, accordo_quadro_id: value, attivita_accordo: '' }));
                            fetchListinoAttivita(value);
                          }}
                          required={tipoCollegamento === 'accordo_quadro'}
                          disabled={editingProgetto}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder="Seleziona accordo quadro" />
                          </SelectTrigger>
                          <SelectContent>
                            {accordiQuadro.map(accordo => (
                              <SelectItem key={accordo.id} value={accordo.id}>
                                {accordo.numero_accordo || 'N/D'} - {accordo.nome_accordo || 'Senza nome'} ({accordo.cliente_nome || 'Cliente'})
                                <span className="ml-2 text-green-600">
                                  Disp: €{(accordo.castelletto_disponibile || 0).toLocaleString('it-IT')}
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}

                  {/* Campi Codice Progetto - Visibili per ordini con più commesse */}
            {tipoCollegamento === 'ordine' && formData.ordine_lavoro_id && ordini.find(o => o.id === formData.ordine_lavoro_id)?.multiple_commesse && (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 space-y-3">
                <div className="flex items-center gap-2 text-purple-700 font-medium">
                  <span>📋</span>
                  <span>Codici specifici per questa commessa</span>
                </div>
                <p className="text-sm text-purple-600">
                  Ordine con più commesse. Inserisci i codici specifici per questo progetto.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="codice_progetto_cliente">Codice Progetto Cliente *</Label>
                    <Input
                      id="codice_progetto_cliente"
                      name="codice_progetto_cliente"
                      value={formData.codice_progetto_cliente}
                      onChange={handleInputChange}
                      required
                      className="mt-1"
                      placeholder="Codice specifico per questo progetto"
                    />
                  </div>
                  <div>
                    <Label htmlFor="codice_progetto_main_solution">Codice Progetto Main Solution *</Label>
                    <Input
                      id="codice_progetto_main_solution"
                      name="codice_progetto_main_solution"
                      value={formData.codice_progetto_main_solution}
                      onChange={handleInputChange}
                      required
                      className="mt-1"
                      placeholder="Codice MS per questo progetto"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Campi Codice Progetto - Visibili per Accordi Quadro */}
                  {tipoCollegamento === 'accordo_quadro' && formData.accordo_quadro_id && (
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 space-y-3">
                      <div className="flex items-center gap-2 text-purple-700 font-medium">
                        <span>📋</span>
                        <span>Codici progetto</span>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="codice_progetto_cliente">Codice Progetto Cliente</Label>
                          <Input
                            id="codice_progetto_cliente"
                            name="codice_progetto_cliente"
                            value={formData.codice_progetto_cliente}
                            onChange={handleInputChange}
                            className="mt-1"
                            placeholder="Codice cliente per questo progetto"
                          />
                        </div>
                        <div>
                          <Label htmlFor="codice_progetto_main_solution">Codice Progetto Main Solution</Label>
                          <Input
                            id="codice_progetto_main_solution"
                            name="codice_progetto_main_solution"
                            value={formData.codice_progetto_main_solution}
                            onChange={handleInputChange}
                            className="mt-1"
                            placeholder="Codice MS per questo progetto"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  <div>
                    <Label htmlFor="nome_progetto">Nome Progetto *</Label>
                    <Input
                      id="nome_progetto"
                      name="nome_progetto"
                      value={formData.nome_progetto}
                      onChange={handleInputChange}
                      required
                      className="mt-1"
                    />
                  </div>

                  {/* Per Accordi Quadro: Dropdown Attività dalla Tabella Prezzi */}
                  {tipoCollegamento === 'accordo_quadro' ? (
                    <div>
                      <Label>Attività *</Label>
                      {loadingListino ? (
                        <div className="mt-1 p-3 border rounded-md text-center text-slate-500">
                          <span className="animate-pulse">Caricamento attività...</span>
                        </div>
                      ) : listinoAttivitaAQ.length === 0 ? (
                        <div className="mt-1 p-3 border rounded-md text-center text-amber-600 bg-amber-50">
                          <p className="text-sm font-medium">Nessuna attività configurata per questo Accordo Quadro</p>
                          <p className="text-xs text-amber-500">Vai alla sezione Accordi Quadro → Listino per aggiungere le attività</p>
                        </div>
                      ) : (
                        <Select 
                          value={formData.attivita_accordo} 
                          onValueChange={(value) => {
                            const attivita = listinoAttivitaAQ.find(a => a.id === value);
                            // Calcola l'importo in base all'unità di misura
                            let importoCalcolato = '';
                            if (attivita) {
                              if (attivita.unita_misura === 'euro') {
                                importoCalcolato = attivita.costo.toString();
                              } else if (attivita.unita_misura === 'euro_ora' && attivita.ore) {
                                importoCalcolato = (attivita.costo * attivita.ore).toString();
                              } else if (attivita.unita_misura === 'ore') {
                                importoCalcolato = attivita.ore?.toString() || '';
                              }
                            }
                            setFormData({
                              ...formData, 
                              attivita_accordo: value,
                              voce_listino_nome: attivita?.nome || '',
                              importo_progetto: importoCalcolato
                            });
                          }}
                          required
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder="Seleziona attività" />
                          </SelectTrigger>
                          <SelectContent>
                            {listinoAttivitaAQ.map((attivita, idx) => (
                              <SelectItem key={attivita.id} value={attivita.id}>
                                <div className="flex justify-between items-center w-full gap-3">
                                  <span>{idx + 1}. {attivita.nome}</span>
                                  {canViewCosti() && (
                                    <span className={`font-semibold ${
                                      attivita.unita_misura === 'ore' ? 'text-blue-600' : 'text-green-600'
                                    }`}>
                                      {attivita.unita_misura === 'ore' 
                                        ? `${attivita.ore || 0}h`
                                        : attivita.unita_misura === 'euro_ora'
                                          ? `€${attivita.costo}/h ${attivita.ore ? `(${attivita.ore}h)` : ''}`
                                          : `€ ${attivita.costo}`
                                      }
                                    </span>
                                  )}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      {formData.attivita_accordo && canViewCosti() && (() => {
                        const attivita = listinoAttivitaAQ.find(a => a.id === formData.attivita_accordo);
                        if (!attivita) return null;
                        return (
                          <p className="text-xs text-green-600 mt-1 font-medium">
                            {attivita.unita_misura === 'ore' 
                              ? `Ore previste: ${attivita.ore || 0}h`
                              : attivita.unita_misura === 'euro_ora'
                                ? `Tariffa: €${attivita.costo}/h ${attivita.ore ? `× ${attivita.ore}h = €${attivita.costo * attivita.ore}` : ''}`
                                : `Costo attività: € ${attivita.costo}`
                            }
                          </p>
                        );
                      })()}
                    </div>
                  ) : (
                    /* Per Ordini: Tipo Impianto e Tipologia Progetto */
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label>Tipo Impianto *</Label>
                        <Select 
                          value={formData.tipo_impianto} 
                          onValueChange={(value) => setFormData({...formData, tipo_impianto: value})}
                          required
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder="Seleziona tipo" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Fotovoltaico">Fotovoltaico</SelectItem>
                            <SelectItem value="Eolico">Eolico</SelectItem>
                            <SelectItem value="BESS">BESS</SelectItem>
                            <SelectItem value="Idroelettrico">Idroelettrico</SelectItem>
                            <SelectItem value="Agrivoltaico">Agrivoltaico</SelectItem>
                            <SelectItem value="Altro">Altro</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Tipologia Progetto</Label>
                        <Select 
                          value={formData.tipologia_progetto} 
                          onValueChange={(value) => setFormData({...formData, tipologia_progetto: value})}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder="Seleziona tipologia" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Preliminare">Preliminare</SelectItem>
                            <SelectItem value="Definitivo">Definitivo</SelectItem>
                            <SelectItem value="Esecutivo">Esecutivo</SelectItem>
                            <SelectItem value="As-Built">As-Built</SelectItem>
                            <SelectItem value="Variante">Variante</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label>Stato *</Label>
                      <Select 
                        value={formData.stato} 
                        onValueChange={(value) => setFormData({...formData, stato: value})}
                        required
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="non_iniziato">Non Iniziato</SelectItem>
                          <SelectItem value="in_corso">In Corso</SelectItem>
                          <SelectItem value="in_validazione">In Validazione</SelectItem>
                          <SelectItem value="in_revisione">In Revisione</SelectItem>
                          <SelectItem value="completato">Completato</SelectItem>
                          <SelectItem value="as_built">As Built</SelectItem>
                          <SelectItem value="sospeso">Sospeso</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Varianti</Label>
                      <Input
                        name="varianti"
                        value={formData.varianti}
                        onChange={handleInputChange}
                        placeholder="Es: Layout modificato, cavi aggiuntivi..."
                        className="mt-1"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="data_inizio">
                        Data Inizio {tipoCollegamento === 'ordine' && user.ruolo !== 'admin' && '(automatica)'}
                      </Label>
                      <Input
                        id="data_inizio"
                        name="data_inizio"
                        type="date"
                        value={formData.data_inizio}
                        onChange={handleInputChange}
                        disabled={tipoCollegamento === 'ordine' && user.ruolo !== 'admin'}
                        className="mt-1"
                      />
                      {tipoCollegamento === 'ordine' && user.ruolo !== 'admin' && (
                        <p className="text-xs text-slate-500 mt-1">Presa da ordine di lavoro</p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="data_fine_prevista">
                        Data Fine Prevista {tipoCollegamento === 'ordine' && user.ruolo !== 'admin' && '(automatica)'}
                      </Label>
                      <Input
                        id="data_fine_prevista"
                        name="data_fine_prevista"
                        type="date"
                        value={formData.data_fine_prevista}
                        onChange={handleInputChange}
                        disabled={tipoCollegamento === 'ordine' && user.ruolo !== 'admin'}
                        className="mt-1"
                      />
                      {tipoCollegamento === 'ordine' && user.ruolo !== 'admin' && (
                        <p className="text-xs text-slate-500 mt-1">Presa da ordine di lavoro</p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="data_fine_effettiva">Data Fine Effettiva</Label>
                      <Input
                        id="data_fine_effettiva"
                        name="data_fine_effettiva"
                        type="date"
                        value={formData.data_fine_effettiva}
                        onChange={handleInputChange}
                        className="mt-1"
                      />
                    </div>
                  </div>

                  <div>
                    <Label>
                      {tipoCollegamento === 'accordo_quadro' ? 'Responsabile Fattibilità *' : 'Ingegnere Responsabile *'}
                    </Label>
                    <Select 
                      value={formData.ingegnere_responsabile_id} 
                      onValueChange={(value) => setFormData({...formData, ingegnere_responsabile_id: value})}
                      required={!editingProgetto}  // Non obbligatorio se modifica (mantiene esistente)
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder={tipoCollegamento === 'accordo_quadro' ? 'Seleziona responsabile fattibilità' : 'Seleziona ingegnere'} />
                      </SelectTrigger>
                      <SelectContent>
                        {/* Mostra sempre l'utente attualmente assegnato se in modifica */}
                        {editingProgetto && formData.ingegnere_responsabile_id && 
                         !getUtentiResponsabile().find(u => u.id === formData.ingegnere_responsabile_id) && (
                          <SelectItem key={formData.ingegnere_responsabile_id} value={formData.ingegnere_responsabile_id}>
                            {editingProgetto.ingegnere_responsabile_nome || 'Utente assegnato'}
                            <span className="ml-2 text-xs text-slate-400">(attuale)</span>
                          </SelectItem>
                        )}
                        {getUtentiResponsabile().map(utente => (
                          <SelectItem key={utente.id} value={utente.id}>
                            {utente.nome || 'N/D'} {utente.cognome || ''}
                            <span className="ml-2 text-xs text-slate-400">
                              ({utente.ruolo || 'ruolo'} - {utente.ambito || 'ambito'})
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {tipoCollegamento === 'accordo_quadro' && getUtentiResponsabile().length === 0 && !editingProgetto && (
                      <p className="text-xs text-amber-600 mt-1">Nessun Master Fattibilità disponibile. Creare un utente con ruolo Master e ambito Fattibilità.</p>
                    )}
                  </div>

                  <div>
                    <Label className="mb-2 block">Referenti di Progetto</Label>
                    <div className="border rounded-lg p-3 max-h-48 overflow-y-auto bg-slate-50">
                      {getUtentiReferenti().length > 0 ? (
                        <div className="space-y-2">
                          {getUtentiReferenti().map(utente => (
                            <label
                              key={utente.id}
                              className="flex items-center gap-2 cursor-pointer hover:bg-slate-100 p-2 rounded"
                            >
                              <input
                                type="checkbox"
                                checked={formData.referenti_progetto_ids.includes(utente.id)}
                                onChange={() => handleToggleReferente(utente.id)}
                                className="w-4 h-4"
                              />
                              <span className="text-sm">
                                {utente.nome || 'N/D'} {utente.cognome || ''}
                                <span className="ml-2 text-xs text-slate-400">
                                  ({utente.ruolo || 'ruolo'} - {utente.ambito || 'ambito'})
                                </span>
                              </span>
                            </label>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-slate-500">
                          {tipoCollegamento === 'accordo_quadro' 
                            ? 'Nessun utente fattibilità disponibile' 
                            : 'Nessun ingegnere disponibile'}
                        </p>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      Selezionati: {formData.referenti_progetto_ids.length}
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="note_tecniche">Note Tecniche</Label>
                    <Textarea
                      id="note_tecniche"
                      name="note_tecniche"
                      value={formData.note_tecniche}
                      onChange={handleInputChange}
                      rows={4}
                      className="mt-1"
                    />
                  </div>

                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                      Annulla
                    </Button>
                    <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700">
                      {editingProgetto ? 'Aggiorna' : 'Salva'}
                    </Button>
                  </div>
                </form>
                </ErrorBoundary>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>

      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          {selectedProgetto && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl">
                  {selectedProgetto.numero_progetto} - {selectedProgetto.nome_progetto}
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-6 mt-4">
                <div className="border-t pt-4">
                  <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                    <Paperclip className="w-5 h-5" />
                    Allegati Tecnici
                  </h3>

                  <div className="bg-slate-50 rounded-lg p-4 mb-4">
                    <Label className="text-sm font-medium mb-2 block">Carica Allegato Tecnico</Label>
                    <div className="flex gap-2">
                      <Input
                        type="text"
                        placeholder="Descrizione"
                        value={uploadDescrizione}
                        onChange={(e) => setUploadDescrizione(e.target.value)}
                        className="flex-1"
                      />
                      <Input
                        type="file"
                        onChange={(e) => setUploadFile(e.target.files[0])}
                        className="flex-1"
                      />
                      <Button
                        onClick={() => handleUploadAllegato(selectedProgetto.id)}
                        className="bg-indigo-600 hover:bg-indigo-700"
                        disabled={!uploadFile}
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        Carica
                      </Button>
                    </div>
                  </div>

                  {selectedProgetto.allegati_tecnici && selectedProgetto.allegati_tecnici.length > 0 ? (
                    <div className="space-y-2">
                      {selectedProgetto.allegati_tecnici.map((allegato) => (
                        <div
                          key={allegato.id}
                          className="flex items-center justify-between p-3 bg-white border rounded-lg"
                        >
                          <div className="flex-1">
                            <p className="font-medium text-sm">{allegato.descrizione || 'Senza descrizione'}</p>
                            <p className="text-xs text-slate-500">{allegato.nome_file}</p>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDownloadAllegato(selectedProgetto.id, allegato.id, allegato.nome_file)}
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteAllegato(selectedProgetto.id, allegato.id)}
                              className="hover:bg-red-50 hover:text-red-600"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500 text-center py-4">Nessun allegato</p>
                  )}
                </div>
                
                {/* Storico Modifiche Date (solo per progetti da Accordi Quadro) */}
                {selectedProgetto.accordo_quadro_id && selectedProgetto.storico_modifiche_date && selectedProgetto.storico_modifiche_date.length > 0 && (
                  <div className="mt-6">
                    <h4 className="font-semibold text-amber-700 mb-2 flex items-center gap-2">
                      <Settings className="w-4 h-4" />
                      Storico Modifiche Date
                    </h4>
                    <div className="border border-amber-200 rounded-lg overflow-hidden">
                      <div className="bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800 grid grid-cols-5 gap-2">
                        <span>Data Modifica</span>
                        <span>Campo</span>
                        <span>Da</span>
                        <span>A</span>
                        <span>Motivazione</span>
                      </div>
                      <div className="divide-y divide-amber-100">
                        {selectedProgetto.storico_modifiche_date.map((mod, idx) => (
                          <div key={idx} className="px-3 py-2 text-xs grid grid-cols-5 gap-2 bg-white hover:bg-amber-50">
                            <span className="text-slate-600">
                              {new Date(mod.data_modifica).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </span>
                            <span className="font-medium text-amber-700">
                              {mod.campo === 'data_fine_prevista' ? 'Fine Prevista' : 'Inizio'}
                            </span>
                            <span className="font-mono text-red-500 line-through">
                              {mod.valore_precedente}
                            </span>
                            <span className="font-mono text-green-600 font-medium">
                              {mod.nuovo_valore}
                            </span>
                            <span className="text-slate-700" title={mod.giustificazione}>
                              {mod.giustificazione.length > 30 ? mod.giustificazione.slice(0, 30) + '...' : mod.giustificazione}
                              <br />
                              <span className="text-slate-400 italic">- {mod.utente}</span>
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-3 py-4 sm:px-6 sm:py-8">
        {loading ? (
          <div className="text-center py-12">
            <p className="text-slate-600">Caricamento...</p>
          </div>
        ) : progetti.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border p-12 text-center">
            <Settings className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">Nessun progetto</h3>
            <p className="text-slate-600 mb-6">Crea il tuo primo progetto collegato a un ordine di lavoro</p>
            <Button onClick={() => setDialogOpen(true)} className="bg-indigo-600 hover:bg-indigo-700">
              <Plus className="w-4 h-4 mr-2" />
              Nuovo Progetto
            </Button>
          </div>
        ) : (
          <>
            {/* Filtro Stato */}
            <div className="mb-4 flex items-center gap-4">
              <label className="text-sm font-medium text-slate-700">Filtra per stato:</label>
              <Select value={filtroStato} onValueChange={setFiltroStato}>
                <SelectTrigger className="w-64">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="tutti">Tutti</SelectItem>
                  <SelectItem value="non_iniziato">Non Iniziato</SelectItem>
                  <SelectItem value="in_corso">In Corso</SelectItem>
                  <SelectItem value="in_validazione">In Validazione</SelectItem>
                  <SelectItem value="in_revisione">In Revisione</SelectItem>
                  <SelectItem value="completato">Completato</SelectItem>
                  <SelectItem value="as_built">As Built</SelectItem>
                  <SelectItem value="sospeso">Sospeso</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-sm text-slate-600">
                {progettiFiltered.length} progett{progettiFiltered.length === 1 ? 'o' : 'i'}
              </span>
            </div>

            <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-semibold">N° Progetto</TableHead>
                  <TableHead className="font-semibold">N° Ordine</TableHead>
                  <TableHead className="font-semibold">Codice Cliente</TableHead>
                  <TableHead className="font-semibold">Codice MS</TableHead>
                  <TableHead className="font-semibold">Nome Progetto</TableHead>
                  <TableHead className="font-semibold">Tipo Impianto</TableHead>
                  <TableHead className="font-semibold">Stato</TableHead>
                  <TableHead className="font-semibold">Ingegnere Resp.</TableHead>
                  <TableHead className="font-semibold">Referenti</TableHead>
                  <TableHead className="text-right font-semibold">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {progettiFiltered.map((progetto) => (
                  <TableRow key={progetto.id}>
                    <TableCell className="font-mono font-semibold text-indigo-600">
                      {progetto.numero_progetto}
                    </TableCell>
                    <TableCell>
                      <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-mono font-medium">
                        📋 {progetto.ordine_numero}
                      </span>
                    </TableCell>
                    <TableCell className="font-mono text-sm text-blue-600">
                      {progetto.codice_progetto_cliente || '-'}
                    </TableCell>
                    <TableCell className="font-mono text-sm text-purple-600">
                      {progetto.codice_progetto_main_solution || '-'}
                    </TableCell>
                    <TableCell className="font-medium">{progetto.nome_progetto}</TableCell>
                    <TableCell>{progetto.tipo_impianto}</TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatoColor(progetto.stato)}`}>
                        {getStatoLabel(progetto.stato)}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm">{progetto.ingegnere_responsabile_nome}</TableCell>
                    <TableCell className="text-xs">
                      {progetto.referenti_progetto && progetto.referenti_progetto.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {progetto.referenti_progetto.slice(0, 2).map((ref, idx) => (
                            <span key={idx} className="px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full text-xs">
                              {ref.nome_completo.split(' ')[0]}
                            </span>
                          ))}
                          {progetto.referenti_progetto.length > 2 && (
                            <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full text-xs">
                              +{progetto.referenti_progetto.length - 2}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex flex-wrap justify-end gap-2">
                        {/* Pulsante Genera Fattura - solo per admin e ingegnere responsabile, solo se progetto completato */}
                        {canGeneraFattura(progetto) && progetto.ordine_lavoro_id && progetto.stato === 'completato' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenGeneraFattura(progetto)}
                            className="hover:bg-emerald-50 hover:text-emerald-600"
                            title="Genera Fattura - Seleziona SAL"
                          >
                            <Euro className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewDetails(progetto)}
                          className="hover:bg-indigo-50 hover:text-indigo-600"
                        >
                          <Paperclip className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(progetto)}
                          className="hover:bg-green-50 hover:text-green-600"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(progetto.id)}
                          className="hover:bg-red-50 hover:text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          </>
        )}
      </div>

      {/* Modale Fattura Generata */}
      <Dialog open={fatturaDialogOpen} onOpenChange={setFatturaDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-green-700">
              {datiFattura ? '✓ Fattura Generata con Successo' : '📋 Genera Fattura'}
            </DialogTitle>
          </DialogHeader>
          
          {!datiFattura && selectedProgetto ? (
            /* Form Inserimento ODA */
            <div className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-bold text-blue-900 mb-2">Progetto Selezionato</h3>
                <p className="text-sm"><strong>N° Progetto:</strong> {selectedProgetto.numero_progetto}</p>
                <p className="text-sm"><strong>Nome:</strong> {selectedProgetto.nome_progetto}</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="codice_oda" className="text-base font-semibold">
                  Codice ODA / Codice Validazione <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="codice_oda"
                  value={codiceOda}
                  onChange={(e) => setCodiceOda(e.target.value)}
                  placeholder="Inserisci il codice ODA o codice validazione"
                  className="text-base"
                  autoFocus
                />
                <p className="text-xs text-slate-500">
                  Questo codice verrà incluso nella fattura e serve per la validazione del pagamento
                </p>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => {
                    setFatturaDialogOpen(false);
                    setSelectedProgetto(null);
                    setCodiceOda('');
                  }}
                >
                  Annulla
                </Button>
                <Button
                  className="bg-green-600 hover:bg-green-700"
                  onClick={handleGeneraFattura}
                  disabled={!codiceOda || codiceOda.trim() === ''}
                >
                  Genera Fattura
                </Button>
              </div>
            </div>
          ) : datiFattura ? (
            /* Dati Fattura Generata */
            <div className="space-y-6">
              {/* Numero Fattura */}
              <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4 text-center">
                <p className="text-sm text-green-700 font-medium mb-1">Numero Fattura</p>
                <p className="text-3xl font-bold text-green-800">{datiFattura.numero_fattura}</p>
              </div>

              {/* Dati Progetto */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-bold text-blue-900 mb-3 flex items-center gap-2">
                  📋 Dati Progetto
                </h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-blue-700 font-medium">Numero Progetto:</p>
                    <p className="font-mono font-semibold">{datiFattura.progetto.numero_progetto}</p>
                  </div>
                  <div>
                    <p className="text-blue-700 font-medium">Nome Progetto:</p>
                    <p className="font-semibold">{datiFattura.progetto.nome_progetto}</p>
                  </div>
                  <div>
                    <p className="text-blue-700 font-medium">Codice Cliente:</p>
                    <p className="font-mono">{datiFattura.progetto.codice_progetto_cliente || '-'}</p>
                  </div>
                  <div>
                    <p className="text-blue-700 font-medium">Codice Main Solution:</p>
                    <p className="font-mono">{datiFattura.progetto.codice_progetto_main_solution || '-'}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-blue-700 font-medium">Codice ODA:</p>
                    <p className="font-mono font-bold text-lg text-blue-900">{datiFattura.progetto.codice_oda}</p>
                  </div>
                </div>
              </div>

              {/* Dati Cliente/Azienda */}
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <h3 className="font-bold text-purple-900 mb-3 flex items-center gap-2">
                  🏢 Dati Azienda Cliente
                </h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="col-span-2">
                    <p className="text-purple-700 font-medium">Ragione Sociale:</p>
                    <p className="font-bold text-lg">{datiFattura.cliente.ragione_sociale}</p>
                  </div>
                  <div>
                    <p className="text-purple-700 font-medium">Partita IVA:</p>
                    <p className="font-mono">{datiFattura.cliente.partita_iva || '-'}</p>
                  </div>
                  <div>
                    <p className="text-purple-700 font-medium">Codice Fiscale:</p>
                    <p className="font-mono">{datiFattura.cliente.codice_fiscale || '-'}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-purple-700 font-medium">Indirizzo:</p>
                    <p>{datiFattura.cliente.indirizzo || '-'}</p>
                    <p>{datiFattura.cliente.cap} {datiFattura.cliente.citta} ({datiFattura.cliente.provincia})</p>
                  </div>
                </div>
              </div>

              {/* Dati Ordine */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <h3 className="font-bold text-amber-900 mb-3 flex items-center gap-2">
                  📝 Dati Ordine di Lavoro
                </h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-amber-700 font-medium">Numero Ordine:</p>
                    <p className="font-mono font-semibold">{datiFattura.ordine.numero_ordine}</p>
                  </div>
                  <div>
                    <p className="text-amber-700 font-medium">Importo Totale:</p>
                    <p className="font-bold text-lg text-green-700">
                      € {datiFattura.ordine.importo_totale.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                </div>
              </div>

              {/* Pulsanti */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => {
                    setFatturaDialogOpen(false);
                    setDatiFattura(null);
                    setSelectedProgetto(null);
                    setCodiceOda('');
                  }}
                >
                  Chiudi
                </Button>
                <Button
                  className="bg-blue-600 hover:bg-blue-700"
                  onClick={() => window.print()}
                >
                  Stampa Fattura
                </Button>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
      
      {/* Dialog per giustificazione modifica date */}
      <Dialog open={modificaDataDialogOpen} onOpenChange={handleAnnullaModificaData}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <Settings className="w-5 h-5" />
              Giustificazione Modifica Data
            </DialogTitle>
          </DialogHeader>
          
          {modificaDataPendente && (
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm text-amber-800 font-medium mb-2">
                  Stai modificando: {modificaDataPendente.label}
                </p>
                <div className="flex items-center gap-4 text-sm">
                  <div>
                    <span className="text-slate-500">Da:</span>
                    <span className="ml-2 font-mono text-red-600 line-through">
                      {new Date(modificaDataPendente.vecchioValore).toLocaleDateString('it-IT')}
                    </span>
                  </div>
                  <span className="text-slate-400">→</span>
                  <div>
                    <span className="text-slate-500">A:</span>
                    <span className="ml-2 font-mono text-green-600 font-semibold">
                      {new Date(modificaDataPendente.nuovoValore).toLocaleDateString('it-IT')}
                    </span>
                  </div>
                </div>
              </div>
              
              <div>
                <Label htmlFor="giustificazione">Motivazione della modifica *</Label>
                <Textarea
                  id="giustificazione"
                  value={giustificazioneModifica}
                  onChange={(e) => setGiustificazioneModifica(e.target.value)}
                  placeholder="Descrivi il motivo della modifica della data..."
                  className="mt-1"
                  rows={3}
                />
                <p className="text-xs text-slate-500 mt-1">
                  Questa informazione verrà registrata nello storico del progetto
                </p>
              </div>
              
              <div className="flex justify-end gap-3 pt-2">
                <Button variant="outline" onClick={handleAnnullaModificaData}>
                  Annulla
                </Button>
                <Button 
                  onClick={handleConfermaModificaData}
                  className="bg-amber-600 hover:bg-amber-700"
                >
                  Conferma Modifica
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Genera Fattura - Selezione SAL */}
      <Dialog open={generaFatturaDialogOpen} onOpenChange={(open) => {
        if (!open) {
          setGeneraFatturaDialogOpen(false);
          setProgettoPerFattura(null);
          setSelectedSalIndex(null);
          setSalListOrdine([]);
        }
      }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-emerald-600" />
              Genera Fattura - Seleziona SAL
            </DialogTitle>
          </DialogHeader>
          
          {progettoPerFattura && (
            <div className="space-y-4">
              {/* Info Progetto */}
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-600">Progetto:</p>
                <p className="font-semibold">{progettoPerFattura.nome_progetto}</p>
                <p className="text-xs text-slate-500 mt-1">
                  Codice: {progettoPerFattura.codice_progetto_main_solution || progettoPerFattura.codice_progetto_cliente || '-'}
                </p>
              </div>
              
              {/* Lista SAL */}
              <div>
                <Label className="mb-2 block">Seleziona il SAL da fatturare:</Label>
                
                {loadingSal ? (
                  <div className="text-center py-4">
                    <span className="animate-pulse">Caricamento SAL...</span>
                  </div>
                ) : salListOrdine.length === 0 ? (
                  <div className="text-center py-4 text-slate-500">
                    Nessun SAL presente nell'ordine di lavoro
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {salListOrdine.map((sal, index) => (
                      <div
                        key={index}
                        onClick={() => sal.stato !== 'fatturato' && setSelectedSalIndex(index)}
                        className={`p-3 border rounded-lg cursor-pointer transition-all ${
                          selectedSalIndex === index 
                            ? 'border-emerald-500 bg-emerald-50' 
                            : sal.stato === 'fatturato'
                              ? 'border-slate-200 bg-slate-100 cursor-not-allowed opacity-60'
                              : sal.stato === 'da_fatturare'
                                ? 'border-yellow-300 bg-yellow-50'
                                : 'border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/50'
                        }`}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-semibold">SAL {sal.numero_sal}</p>
                            <p className="text-sm text-slate-600">{sal.descrizione || 'Senza descrizione'}</p>
                            <div className="flex gap-3 mt-1 text-xs">
                              <span className="text-slate-500">{sal.percentuale}%</span>
                              <span className="font-semibold text-emerald-700">
                                € {sal.importo?.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                              </span>
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-1">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              sal.stato === 'fatturato' ? 'bg-green-100 text-green-700' :
                              sal.stato === 'da_fatturare' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-slate-100 text-slate-600'
                            }`}>
                              {sal.stato === 'fatturato' ? '✓ Fatturato' :
                               sal.stato === 'da_fatturare' ? '⏳ Da Fatturare' :
                               '− Non Fatturabile'}
                            </span>
                            {/* Pulsante annulla per SAL già marcati come da_fatturare */}
                            {sal.stato === 'da_fatturare' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAnnullaStatoSal(index);
                                }}
                                className="text-xs text-red-600 hover:bg-red-50 h-6 px-2"
                              >
                                <Undo2 className="w-3 h-3 mr-1" />
                                Annulla
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Azioni */}
              <div className="flex justify-end gap-3 pt-2 border-t">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setGeneraFatturaDialogOpen(false);
                    setProgettoPerFattura(null);
                    setSelectedSalIndex(null);
                  }}
                >
                  Annulla
                </Button>
                <Button 
                  onClick={handleConfermaGeneraFattura}
                  disabled={selectedSalIndex === null}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Imposta come Da Fatturare
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProgettiPage;
